/**
 * Geometry utilities for polyline interpolation using real-world distances.
 * Supports stop/dwell durations at waypoints and multi-segment agents.
 */

import type { Agent, Segment, SimTime, Waypoint } from "../types"
import { secondsToSimTime, simTimeToSeconds } from "../types"

const EARTH_RADIUS = 6371000 // meters

export function haversineDistance(a: [number, number], b: [number, number]): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const dLat = toRad(b[0] - a[0])
    const dLon = toRad(b[1] - a[1])
    const lat1 = toRad(a[0])
    const lat2 = toRad(b[0])
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
    return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(x))
}

/** Duration of a single segment in seconds (travel + stops) */
export function totalSegmentDuration(waypoints: Waypoint[], defaultSpeed: number): number {
    if (waypoints.length < 2 || defaultSpeed <= 0) return 0
    let duration = 0
    duration += waypoints[0].stopDuration
    for (let i = 1; i < waypoints.length; i++) {
        const dist = haversineDistance(waypoints[i - 1].position, waypoints[i].position)
        // Use waypoint-specific speed if defined, otherwise use default
        const segmentSpeed = waypoints[i - 1].speed ?? defaultSpeed
        duration += dist / (segmentSpeed > 0 ? segmentSpeed : defaultSpeed)
        duration += waypoints[i].stopDuration
    }
    return duration
}

/** Effective start seconds for a segment */
export function segmentEffectiveStart(seg: Segment, speed: number): number {
    if (seg.timeMode === "start") return simTimeToSeconds(seg.startTime)
    return Math.max(0, simTimeToSeconds(seg.endTime) - totalSegmentDuration(seg.waypoints, speed))
}

/** Effective end seconds for a segment */
export function segmentEffectiveEnd(seg: Segment, speed: number): number {
    if (seg.timeMode === "end") return simTimeToSeconds(seg.endTime)
    return simTimeToSeconds(seg.startTime) + totalSegmentDuration(seg.waypoints, speed)
}

/**
 * Interpolate within a single segment given elapsed seconds since segment start.
 */
export function interpolateWithinSegment(
    waypoints: Waypoint[],
    elapsedSeconds: number,
    defaultSpeed: number
): [number, number] | null {
    if (waypoints.length === 0) return null
    if (waypoints.length === 1) return waypoints[0].position
    if (elapsedSeconds <= 0 || defaultSpeed <= 0) return waypoints[0].position

    let remaining = elapsedSeconds

    // Dwell at first waypoint
    if (remaining <= waypoints[0].stopDuration) return waypoints[0].position
    remaining -= waypoints[0].stopDuration

    for (let i = 1; i < waypoints.length; i++) {
        const dist = haversineDistance(waypoints[i - 1].position, waypoints[i].position)
        // Use waypoint-specific speed if defined, otherwise use default
        const segmentSpeed = waypoints[i - 1].speed ?? defaultSpeed
        const actualSpeed = segmentSpeed > 0 ? segmentSpeed : defaultSpeed
        const travelTime = dist / actualSpeed
        if (remaining <= travelTime) {
            const t = travelTime === 0 ? 0 : remaining / travelTime
            return [
                waypoints[i - 1].position[0] + t * (waypoints[i].position[0] - waypoints[i - 1].position[0]),
                waypoints[i - 1].position[1] + t * (waypoints[i].position[1] - waypoints[i - 1].position[1]),
            ]
        }
        remaining -= travelTime
        if (remaining <= waypoints[i].stopDuration) return waypoints[i].position
        remaining -= waypoints[i].stopDuration
    }

    return waypoints[waypoints.length - 1].position
}

/**
 * Compute agent position at a given simulation time (seconds since midnight).
 * Handles multi-segment agents.
 */
export function computeAgentPosition(
    agent: Agent,
    simSeconds: number
): { position: [number, number] | null; visible: boolean; finished: boolean } {
    if (agent.segments.length === 0) {
        return { position: null, visible: false, finished: false }
    }

    // Check if before first segment
    const firstStart = segmentEffectiveStart(agent.segments[0], agent.speed)
    if (simSeconds < firstStart) {
        return { position: null, visible: false, finished: false }
    }

    // Check each segment
    let lastKnownPosition: [number, number] | null = null

    for (let i = 0; i < agent.segments.length; i++) {
        const seg = agent.segments[i]
        if (seg.waypoints.length === 0) continue

        const segStart = segmentEffectiveStart(seg, agent.speed)
        const segEnd = segmentEffectiveEnd(seg, agent.speed)

        if (simSeconds < segStart) {
            // Between segments — stay at last known position
            return { position: lastKnownPosition, visible: lastKnownPosition !== null, finished: false }
        }

        if (simSeconds <= segEnd) {
            // Within this segment
            const elapsed = simSeconds - segStart
            const pos = interpolateWithinSegment(seg.waypoints, elapsed, agent.speed)
            return { position: pos, visible: pos !== null, finished: false }
        }

        // Past this segment — record last waypoint
        lastKnownPosition = seg.waypoints[seg.waypoints.length - 1].position
    }

    // Past all segments - check if we should remove
    // Only remove if removeOnEnd is true AND we've passed the last segment's end time
    const lastSegmentEnd = segmentEffectiveEnd(agent.segments[agent.segments.length - 1], agent.speed)
    const shouldRemove = (agent.removeOnEnd ?? false) && simSeconds > lastSegmentEnd

    return {
        position: shouldRemove ? null : lastKnownPosition,
        visible: shouldRemove ? false : lastKnownPosition !== null,
        finished: true,
    }
}

/** Effective end time (seconds) across all segments of an agent */
export function agentEffectiveEnd(agent: Agent): number {
    if (agent.segments.length === 0) return 0
    const last = agent.segments[agent.segments.length - 1]
    return segmentEffectiveEnd(last, agent.speed)
}

/** SimTime version of segmentEffectiveStart */
export function getSegmentEffectiveStartTime(seg: Segment, speed: number): SimTime {
    return secondsToSimTime(segmentEffectiveStart(seg, speed))
}

/** SimTime version of segmentEffectiveEnd */
export function getSegmentEffectiveEndTime(seg: Segment, speed: number): SimTime {
    return secondsToSimTime(segmentEffectiveEnd(seg, speed))
}
